import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SlotPicker, { SlotState } from '../messages/components/SlotPicker';

/**
 * 마켓의 'seeking_member' listing 에서 가입 요청을 시작하는 모달.
 *
 * 의미: 사용자가 본인의 모패밀리(child 가 될 자)를 listing 의 모패밀리(parent 가 될 자)
 * 의 자식으로 들어가도록 요청. parent-initiated invite 와 방향 반대.
 *
 * 흐름:
 *  1. 사용자의 my-families 목록 fetch → 가입할 본인 모패밀리 선택 (= future child)
 *  2. SlotPicker 가 본인 모패밀리의 슬롯 표시 → parent 에게 위임할 슬롯 선택 (D/S)
 *  3. 가격(슬롯당 일일) + 메시지
 *  4. 제출 → POST /mofamily/{my}/request-join-family → 응답의 conversationId 로 /inbox 이동
 *
 * 양측 thread 안에서 협상을 시작 (수락 시 mofamily_relationships parent=target, child=my).
 */

interface MyFamily {
 moFamilyHash: string;
 name: string;
}

interface RecipientInfo {
 mo_peer_hash: string;
 email: string | null;
 name?: string | null;
}

interface Props {
 toFamilyHash: string;        // listing.mo_family_hash (모집하는 패밀리, 미래 parent)
 toFamilyName: string;
 recipientOwner: RecipientInfo;  // listing 의 owner (수신자, parent 측)
 onClose: () => void;
}

const InvitationCreateModal: React.FC<Props> = ({ toFamilyHash, toFamilyName, recipientOwner, onClose }) => {
 const navigate = useNavigate();
 const [families, setFamilies] = useState<MyFamily[] | null>(null);
 const [familiesError, setFamiliesError] = useState<string | null>(null);
 const [fromFamilyHash, setFromFamilyHash] = useState<string>('');
 const [delegation, setDelegation] = useState<SlotState[]>([]);
 const [slotNumbers, setSlotNumbers] = useState<number[]>([]);
 const [price, setPrice] = useState<number>(0);
 const [message, setMessage] = useState<string>('');
 const [submitting, setSubmitting] = useState(false);
 const [submitError, setSubmitError] = useState<string | null>(null);

 useEffect(() => {
  fetch('/mofamily/my-families', { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (!j.success) { setFamiliesError(j.error || '패밀리 조회 실패'); setFamilies([]); return; }
    const list: MyFamily[] = (j.data ?? []).filter((f: any) => f.moFamilyHash !== toFamilyHash);
    setFamilies(list);
    if (list.length > 0) setFromFamilyHash(list[0].moFamilyHash);
   })
   .catch(e => { setFamiliesError(e.message ?? '패밀리 조회 실패'); setFamilies([]); });
 }, [toFamilyHash]);

 const proposedSlotsCount = useMemo(
  () => delegation.filter(s => s !== 'none').length,
  [delegation],
 );

 const onSubmit = async () => {
  if (!fromFamilyHash) { setSubmitError('보낼 모패밀리를 선택하세요.'); return; }
  if (proposedSlotsCount === 0) { setSubmitError('최소 한 슬롯 이상 선택하세요.'); return; }

  const dedicated_slots = delegation.flatMap((s, i) => (s === 'dedicated' ? [slotNumbers[i]] : []));
  const shared_slots = delegation.flatMap((s, i) => (s === 'shared' ? [slotNumbers[i]] : []));
  const sharingType: 'dedicated' | 'shared' = shared_slots.length > 0 ? 'shared' : 'dedicated';

  setSubmitting(true);
  setSubmitError(null);
  try {
   const r = await fetch(`/mofamily/${fromFamilyHash}/request-join-family`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
     targetFamilyHash: toFamilyHash,
     proposedPrice: price,
     proposedSlots: proposedSlotsCount,
     sharingType,
     dedicatedSlots: dedicated_slots,
     sharedSlots: shared_slots,
     message: message || undefined,
    }),
   });
   const j = await r.json();
   if (!j.success) throw new Error(j.error || '가입 요청 전송 실패');
   const cid = j.data?.conversationId;
   if (cid) {
    navigate(`/inbox/${cid}`);
   } else {
    alert('가입 요청을 보냈습니다. /inbox 에서 확인하세요.');
    navigate('/inbox');
   }
  } catch (e: any) {
   setSubmitError(e.message ?? '가입 요청 전송 실패');
  } finally {
   setSubmitting(false);
  }
 };

 return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
   <div className="bg-white p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
    <h2 className="text-xl font-bold mb-1">가입 제안 보내기</h2>
    <p className="text-sm text-gray-500 mb-4">
     <span className="font-medium">{toFamilyName}</span> 모패밀리에 가입 제안 — 본인의 모패밀리를 그 하위로 묶고 슬롯 위임 협상을 시작합니다.
    </p>

    {familiesError && <div className="bg-red-50 text-red-700 p-2 text-sm mb-3">{familiesError}</div>}
    {families && families.length === 0 && (
     <div className="bg-yellow-50 text-yellow-800 p-2 text-sm mb-3">
      소유한 모패밀리가 없습니다. 먼저 모패밀리를 만들어 주세요.
     </div>
    )}

    {families && families.length > 0 && (
     <>
      <div className="mb-3">
       <label className="block text-xs text-gray-600 mb-1">가입할 본인 모패밀리</label>
       <select
        value={fromFamilyHash}
        onChange={e => setFromFamilyHash(e.target.value)}
        disabled={submitting}
        className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
       >
        {families.map(f => <option key={f.moFamilyHash} value={f.moFamilyHash}>{f.name}</option>)}
       </select>
      </div>

      <div className="mb-3">
       <label className="block text-xs text-gray-600 mb-1">
        가입 대상 모패밀리: <span className="font-medium text-gray-700">{toFamilyName}</span>
        {recipientOwner.name && <span className="text-gray-500"> (대표: {recipientOwner.name})</span>}
       </label>
      </div>

      <div className="mb-3">
       <label className="block text-xs text-gray-600 mb-1">
        내 모패밀리 슬롯 위임 (클릭하여 독점 → 공유 → 해제) — 가입 대상에게 위임할 슬롯
       </label>
       {fromFamilyHash && (
        <SlotPicker
         key={fromFamilyHash}
         toFamilyHash={fromFamilyHash}
         selectedFamilyHash={fromFamilyHash}
         initial={{ dedicated: [], shared: [] }}
         defaultFirstDedicated
         onChange={(d, n) => { setDelegation(d); setSlotNumbers(n); }}
         disabled={submitting}
        />
       )}
       <div className="text-xs text-gray-500 mt-1">선택된 슬롯: {proposedSlotsCount}개</div>
      </div>

      <div className="mb-3">
       <label className="block text-xs text-gray-600 mb-1">슬롯당 일일 가격 (원)</label>
       <input
        type="number"
        min={0}
        step={1000}
        value={price}
        onChange={e => setPrice(parseInt(e.target.value) || 0)}
        disabled={submitting}
        className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
       />
      </div>

      <div className="mb-3">
       <label className="block text-xs text-gray-600 mb-1">메시지 (선택)</label>
       <textarea
        rows={3}
        value={message}
        onChange={e => setMessage(e.target.value)}
        disabled={submitting}
        placeholder="가입 제안 사유, 협업 내용 등을 적어 주세요..."
        className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
       />
      </div>

      {submitError && <div className="bg-red-50 text-red-700 p-2 text-sm mb-3">{submitError}</div>}

      <div className="flex gap-2 justify-end mt-4">
       <button
        onClick={onClose}
        disabled={submitting}
        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
       >취소</button>
       <button
        onClick={onSubmit}
        disabled={submitting || proposedSlotsCount === 0}
        className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white text-sm disabled:opacity-50"
       >{submitting ? '전송 중...' : '제안 보내기'}</button>
      </div>
     </>
    )}
   </div>
  </div>
 );
};

export default InvitationCreateModal;
